import { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Announcement,
  Button,
  ExclamationTriangleIcon,
  Layout,
  LoaderSkeleton,
  ModalV2 as Modal,
  TextArea,
} from '@utilities';

import { MinusPlusButtons } from '@utilities';

import styles from './_index.module.scss';
import { createQuestionnaireJob, readQuestionnaireJob } from '@api/instant_survey';
import { SurveyContext } from '../../../../utilities/context';

const ModalAIGeneratedQuestionnaire = ({
  initialSurveyLength,
  onAddAIQuestions,
  onClose,
  onGenerationError,
}) => {
  const { activeQuestions, setJobId, setShowAIGenerationModal, setSurveyGoals, surveyGoals } =
    useContext(SurveyContext);

  const [goals, setGoals] = useState(surveyGoals || '');
  const [isPolling, setIsPolling] = useState(false);
  const [questionnaireLength, setQuestionnaireLength] = useState(initialSurveyLength || 5);

  const abortControllerRef = useRef(null);

  const handleAIQuestionnaireGenerate = async () => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const signal = controller.signal;
    try {
      setIsPolling(true);
      onGenerationError(false);
      const job = await createQuestionnaireJob({ length: questionnaireLength, userInput: goals });
      setJobId(job.id);
      const response = await pollQuestionnaireJob({ jobId: job.id, signal });

      if (response?.output?.questions?.length) {
        onAddAIQuestions(response.output.questions);
        setShowAIGenerationModal(false);
        setSurveyGoals(goals);
      } else {
        onGenerationError(true);
      }
    } catch (error) {
      if (error.message === 'Job was canceled') {
        return;
      }
      setShowAIGenerationModal(false);
      onGenerationError(true);
      setSurveyGoals(goals);
      console.error(error);
    } finally {
      setIsPolling(false);
    }
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    onClose();
  };

  const pollQuestionnaireJob = async ({ jobId, signal }) => {
    const interval = 2000;
    const maxAttempts = 60;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await readQuestionnaireJob({ jobId, signal });

        if (response.status === 'COMPLETE') {
          return response;
        } else if (response.status === 'ERROR') {
          throw new Error('Job failed with status: ERROR');
        }

        if (response.status === 'QUEUED' || response.status === 'PROCESSING') {
          if (attempt < maxAttempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, interval));
          }
          continue;
        }

        console.warn(`Unexpected job status: ${response.status}`);
        await new Promise((resolve) => setTimeout(resolve, interval));
      } catch (error) {
        if (signal.aborted) {
          throw new Error('Job was canceled');
        }

        if (error.response?.status === 404) {
          await new Promise((resolve) => setTimeout(resolve, interval));
          continue;
        }
        throw error;
      }
    }

    throw new Error('Job polling timed out');
  };

  const actionButtons = [
    <Button key="modal-cancel-button" onClick={handleCancel} text="Cancel" variant="secondary" />,
    <Button
      isDisabled={!goals.trim()}
      isLoading={isPolling}
      key="modal-generate-button"
      onClick={handleAIQuestionnaireGenerate}
      text="Generate Questionnaire"
    />,
  ];

  return (
    <Modal
      buttons={actionButtons}
      id="modal-ai-generated-questionnaire"
      isActive
      onClose={handleCancel}
      title="AI Generated Questionnaire"
    >
      <div className={styles['modal-ai-content']}>
        {isPolling ? (
          <div className={styles['modal-ai-loading']}>
            <p>Please allow up to a minute for AI Questions to generate.</p>
            <LoaderSkeleton height={510}>
              <rect height="30" rx="4" ry="4" width="800" x="0" y="20" />
              <rect height="30" rx="4" ry="4" width="800" x="0" y="55" />
              <rect height="30" rx="4" ry="4" width="350" x="0" y="90" />
              <rect height="30" rx="4" ry="4" width="280" x="0" y="150" />
              <rect height="30" rx="4" ry="4" width="280" x="0" y="185" />
              <rect height="30" rx="4" ry="4" width="280" x="0" y="220" />
              <rect height="30" rx="4" ry="4" width="800" x="0" y="280" />
              <rect height="30" rx="4" ry="4" width="800" x="0" y="315" />
              <rect height="30" rx="4" ry="4" width="350" x="0" y="350" />
              <rect height="30" rx="4" ry="4" width="280" x="0" y="410" />
              <rect height="30" rx="4" ry="4" width="280" x="0" y="445" />
              <rect height="30" rx="4" ry="4" width="280" x="0" y="480" />
            </LoaderSkeleton>
          </div>
        ) : (
          <>
            {activeQuestions.length > 0 && (
              <Announcement
                icon={<ExclamationTriangleIcon />}
                text="Your existing survey questions will be replaced."
                variant="warn"
              />
            )}
            <div className={styles['modal-ai-input-group']}>
              <TextArea
                label="GOALS AND OBJECTIVES"
                onChange={setGoals}
                placeholder="What are you looking to learn from this survey?"
                value={goals}
              />
            </div>
            <Layout.Flex className={styles['modal-ai-input-group']} gap="x-small">
              <span className={styles['modal-ai-questionnaire-length-label']}>
                Questionnaire Length
              </span>
              <strong className={styles['modal-ai-questionnaire-length']}>
                {questionnaireLength}
              </strong>
              <MinusPlusButtons
                disableMinusButton={questionnaireLength <= 1}
                disablePlusButton={questionnaireLength >= 10}
                onClickMinus={() => setQuestionnaireLength((prev) => Math.max(prev - 1), 1)}
                onClickPlus={() => setQuestionnaireLength((prev) => Math.max(prev + 1), 10)}
              />
            </Layout.Flex>
            <p className={styles['modal-ai-note']}>
              By proceeding, you consent to the information above being shared with OpenAI to
              generate your survey questions. No account information is shared. Please see the{' '}
              <a href="https://openai.com/policies/terms-of-use/" rel="noreferrer" target="_blank">
                OpenAI Terms
              </a>{' '}
              for more information.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
};

ModalAIGeneratedQuestionnaire.propTypes = {
  initialSurveyLength: PropTypes.number,
  onAddAIQuestions: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onGenerationError: PropTypes.func.isRequired,
};

export default ModalAIGeneratedQuestionnaire;
